/**
 * Brand review of creator earning claims - the campaign Earnings tab.
 */
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Eyebrow, Money, StateBlock } from "@ui/primitives";
import { TextInput, FormError } from "@ui/forms";
import { fontSize, space } from "@design/index";
import { fromMinorString, type CurrencyCode } from "@contracts/money";
import { api } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import { claimLabel, claimTone, formatViews } from "@app/shared/EarningClaim";
import { ConnectedAccountLink } from "./Submissions";
import { postOpenUrl } from "@server/platforms";
import { CreatorAvatar } from "@app/shared/icons";

interface Claim {
  id: string; submission_id: string; creator_id: string;
  views: string; amount_minor: string; currency: string; state: string;
  requested_at: string; reviewed_at: string | null; review_note: string | null;
  platform: string; submitted_url: string; canonical_url: string | null;
  creator_name: string | null; creator_handle?: string | null;
  avatar_href?: string | null;
}

export function CampaignEarningsPanel({
  campaignId, currency,
}: { campaignId: string; currency: string }) {
  const toast = useToast();
  const [rows, setRows] = useState<Claim[] | null | "error">(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<{ claims: Claim[] }>(`/campaigns/${campaignId}/earning-claims`);
    setRows(r.ok ? (r.data.claims ?? []) : "error");
  }, [campaignId]);
  useEffect(() => { void load(); }, [load]);

  async function decide(id: string, decision: "APPROVED" | "REJECTED") {
    setBusy(id); setError(null);
    const r = await api.post(`/earning-claims/${id}/review`, {
      decision, note: note[id] ?? "",
    });
    setBusy(null);
    if (r.ok) {
      toast.ok(decision === "APPROVED" ? "Claim approved. The creator is paid." : "Claim declined.");
      await load();
    } else {
      setError(r.message);
      toast.error(r.message);
    }
  }

  if (rows === null) return <StateBlock kind="loading" title="Loading earnings" />;
  if (rows === "error") return <StateBlock kind="error" title="Could not load earning claims" />;
  if (rows.length === 0) {
    return (
      <StateBlock kind="empty" title="No earning claims yet"
        detail="When a creator’s post reaches 1,000 counted views they can claim. Those requests land here for you to approve." />
    );
  }

  const open = rows.filter((r) => r.state === "REQUESTED");
  const closed = rows.filter((r) => r.state !== "REQUESTED");

  return (
    <div style={{ display: "grid", gap: space(4) }}>
      {error ? <FormError message={error} /> : null}
      {open.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <Eyebrow>Waiting on you</Eyebrow>
          {open.map((cl) => (
            <ClaimCard key={cl.id} claim={cl} currency={currency}
              note={note[cl.id] ?? ""}
              onNote={(v) => setNote((n) => ({ ...n, [cl.id]: v }))}
              busy={busy === cl.id}
              onApprove={() => void decide(cl.id, "APPROVED")}
              onReject={() => void decide(cl.id, "REJECTED")} />
          ))}
        </section>
      ) : null}
      {closed.length > 0 ? (
        <section style={{ display: "grid", gap: 12 }}>
          <Eyebrow>Decided</Eyebrow>
          {closed.map((cl) => (
            <ClaimCard key={cl.id} claim={cl} currency={currency} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function ClaimCard({
  claim, currency, note, onNote, busy, onApprove, onReject,
}: {
  claim: Claim; currency: string;
  note?: string; onNote?: (v: string) => void;
  busy?: boolean; onApprove?: () => void; onReject?: () => void;
}) {
  const ccy = (claim.currency || currency) as CurrencyCode;
  const open = claim.state === "REQUESTED";
  const openUrl = postOpenUrl({
    platform: claim.platform,
    submittedUrl: claim.submitted_url,
    canonicalUrl: claim.canonical_url,
    handle: claim.creator_handle,
  });
  return (
    <Card padding={18} style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CreatorAvatar src={claim.avatar_href} name={claim.creator_name ?? undefined} size={40} />
          <div style={{ display: "grid", gap: 4 }}>
            <strong style={{ fontSize: fontSize.bodyLg }}>{claim.creator_name ?? "A creator"}</strong>
            <ConnectedAccountLink platform={claim.platform} handle={claim.creator_handle ?? null} />
          </div>
        </div>
        <Badge tone={claimTone(claim.state)}>{claimLabel(claim.state, "brand")}</Badge>
      </div>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: fontSize.body }}>
        <span>{formatViews(claim.views)} counted views</span>
        <span>
          <Money value={fromMinorString(claim.amount_minor, ccy)} emphasis />
        </span>
      </div>
      <a href={openUrl} target="_blank" rel="noreferrer noopener"
         style={{ color: "var(--accent)", fontSize: fontSize.label }}>
        {openUrl}
      </a>
      {claim.review_note ? (
        <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--text-2)", lineHeight: 1.5 }}>
          {claim.review_note}
        </p>
      ) : null}
      {open && onApprove && onReject ? (
        <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
          <TextInput label="Note (required to decline)" value={note ?? ""}
            onChange={(e) => onNote?.(e.currentTarget.value)} />
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" variant="primary" disabled={busy} onClick={onApprove}>
              {busy ? "Saving…" : "Approve and pay"}
            </Button>
            <Button size="sm" disabled={busy} onClick={onReject}>Decline</Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
